import {announce} from '@github-ui/aria-live'
import {isLoggedIn} from '@github-ui/client-env'
import {Link} from '@github-ui/react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useAnalytics} from '@github-ui/use-analytics'
import {ChevronDownIcon, ChevronUpIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react'

import type {ItemIdentifier} from '../../types/issue'
import type {ViewNavState} from '../OptionConfig'
import {
  consumePendingNavIndex,
  isApproachingRateLimit,
  isThrottled,
  isVisited,
  markVisited,
  recordNavigation,
  setPendingNavIndex,
} from './nav-rate-limit'
import styles from './ViewPositionIndicator.module.css'

function deriveIndex(items: ItemIdentifier[]): number {
  const path = ssrSafeLocation.pathname
  const match = path.match(/\/([^/]+)\/([^/]+)\/issues\/(\d+)/)
  if (!match) return -1
  const [, owner, repo, numberStr] = match
  if (!owner || !repo || !numberStr) return -1
  const number = parseInt(numberStr, 10)
  return items.findIndex(item => item.owner === owner && item.repo === repo && item.number === number)
}

type ViewPositionIndicatorProps = {
  navigate: (url: string, options?: {state?: {viewNav: ViewNavState}}) => void
  viewNavState?: ViewNavState | null
  onLoadMore?: () => void
  onLoadBack?: () => void
  pendingFocusDirection?: 'prev' | 'next' | null
  // whether the navigation was triggered by J/K, a click, or the Enter/Space key
  pendingNavSource?: 'shortcut' | 'click' | 'activate' | null
  isStickyHeader?: boolean
}

export function ViewPositionIndicator({
  navigate,
  viewNavState,
  onLoadMore,
  onLoadBack,
  pendingFocusDirection,
  pendingNavSource,
  isStickyHeader = false,
}: ViewPositionIndicatorProps) {
  const [currentIndex, setCurrentIndex] = useState(() => {
    if (!viewNavState) return -1
    const pending = consumePendingNavIndex()
    const derived = deriveIndex(viewNavState.items)
    const idx = pending ?? derived
    if (idx >= 0) markVisited(idx)
    return idx
  })
  const [bounceDirection, setBounceDirection] = useState<'prev' | 'next' | null>(
    pendingNavSource === 'shortcut' ? (pendingFocusDirection ?? null) : null,
  )

  const loggedInUser = isLoggedIn()

  const items = useMemo(() => viewNavState?.items ?? [], [viewNavState?.items])
  const viewName = viewNavState?.viewName ?? 'Issues'
  const hasState = viewNavState && currentIndex >= 0
  const {sendAnalyticsEvent} = useAnalytics()

  const announcePosition = useEffectEvent(() => {
    if (currentIndex < 0) return
    const backItemCount = viewNavState?.backItemCount ?? 0
    const positionAnnouncement = `Issue ${backItemCount + currentIndex + 1} of ${viewNavState?.totalCount ?? items.length}`

    // in this case we focus the next/previous buttons, and need to announce the issue title for context.
    if (pendingNavSource === 'activate') {
      const title = document.title.replace(/\s[·•]\s.*$/, '').trim()
      announce(`${title}, ${positionAnnouncement.toLowerCase()}`)
    } else {
      // focus falls to title naturally, so we only need to announce the position
      announce(positionAnnouncement)
    }
  })
  useEffect(() => {
    announcePosition()
  }, [currentIndex, pendingNavSource])

  const prefetchAdjacent = useEffectEvent(() => {
    if (currentIndex === 0 && (viewNavState?.backItemCount ?? 0) > 0) onLoadBack?.()
    if (currentIndex === items.length - 1 && items.length < (viewNavState?.totalCount ?? 0)) onLoadMore?.()
  })
  // On mount pre-fetch adjacent page if the issue is at a boundary
  useEffect(() => {
    prefetchAdjacent()
  }, [])

  // Adjust index when items are prepended (backward loading shifts all indices by prependedCount),
  // not using URL-derived index since URL might be stale with rapid navigations
  const [prevItems, setPrevItems] = useState(items)
  if (items !== prevItems) {
    const grew = items.length > prevItems.length
    const prepended = grew && items[0] !== prevItems[0]
    setPrevItems(items)
    if (prepended) {
      const prependedCount = items.length - prevItems.length
      const pending = consumePendingNavIndex()
      if (pending !== null) {
        setPendingNavIndex(pending + prependedCount)
      }
      setCurrentIndex(prev => prev + prependedCount)
    }
  }

  const handleNav = useCallback(
    (direction: 'prev' | 'next', source: 'shortcut' | 'click' | 'activate') => {
      if (!hasState || isThrottled(loggedInUser)) return

      const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1
      const maxIndex = Math.min(items.length, viewNavState?.totalCount ?? items.length) - 1
      if (newIndex < 0 || newIndex > maxIndex) {
        return
      }

      const isAlreadyCached = isVisited(newIndex)

      // Always record, since additional queries are still run even for cached items
      recordNavigation()

      if (isApproachingRateLimit(loggedInUser)) {
        sendAnalyticsEvent(
          'issue_viewer.view_navigation_rate_limit_mitigated',
          'ISSUE_VIEW_NAVIGATION_RATE_LIMIT_MITIGATED',
          {direction, source, cached: isAlreadyCached},
        )
      }

      markVisited(newIndex)

      sendAnalyticsEvent('issue_viewer.view_navigation', 'ISSUE_VIEW_NAVIGATION', {
        direction,
        source,
      })

      // Trigger loading more when moving forward and within half a page of the end
      const PAGE_HALF = 13
      if (onLoadMore && direction === 'next' && items.length - newIndex <= PAGE_HALF) {
        onLoadMore()
      }

      // Trigger loading backward when moving backward and within half a page of the start
      if (onLoadBack && direction === 'prev' && newIndex <= PAGE_HALF) {
        onLoadBack()
      }

      const item = items[newIndex]!
      const url = `/${item.owner}/${item.repo}/issues/${item.number}`
      setPendingNavIndex(newIndex)
      setCurrentIndex(newIndex)
      navigate(url, {
        state: {viewNav: {...viewNavState, items, pendingFocusDirection: direction, pendingNavSource: source}},
      })
    },
    [hasState, currentIndex, items, navigate, viewNavState, onLoadMore, onLoadBack, sendAnalyticsEvent, loggedInUser],
  )

  const commands = useMemo(
    () =>
      hasState
        ? {
            'issues-react:focus-next-issue': () => handleNav('next', 'shortcut'),
            'issues-react:focus-previous-issue': () => handleNav('prev', 'shortcut'),
          }
        : undefined,
    [hasState, handleNav],
  )

  if (!hasState) return null

  const hasPrev = currentIndex > 0
  const maxIndex = Math.min(items.length, viewNavState?.totalCount ?? items.length) - 1
  const hasNext = currentIndex < maxIndex
  const autoFocusNext = (pendingFocusDirection === 'next' && pendingNavSource === 'activate') || undefined
  const autoFocusPrev = (pendingFocusDirection === 'prev' && pendingNavSource === 'activate') || undefined

  return (
    <>
      {commands && <GlobalCommands commands={commands} />}
      <nav
        aria-label="Issue navigation"
        className={styles.container}
        data-sticky-header={isStickyHeader ? '' : undefined}
      >
        {viewNavState?.returnUrl && !isStickyHeader ? (
          <Link
            className={styles.backButton}
            to={viewNavState.returnUrl}
            state={viewNavState.scrollY !== undefined ? {restoreScrollY: viewNavState.scrollY} : undefined}
          >
            Back to {viewName}
          </Link>
        ) : null}
        {!isStickyHeader && (
          <span className={styles.position} aria-hidden="true">
            {`(${(viewNavState?.backItemCount ?? 0) + currentIndex + 1}/${viewNavState?.totalCount ?? items.length})`}
          </span>
        )}
        {!isStickyHeader && (
          <span className="sr-only">
            {`Issue ${(viewNavState?.backItemCount ?? 0) + currentIndex + 1} of ${viewNavState?.totalCount ?? items.length}`}
          </span>
        )}
        <div className={styles.arrows}>
          <IconButton
            icon={ChevronDownIcon}
            variant="invisible"
            size="small"
            onClick={e => handleNav('next', e.detail === 0 ? 'activate' : 'click')}
            inactive={!hasNext}
            aria-disabled={!hasNext || undefined}
            aria-label="Next issue"
            keybindingHint="j"
            className={bounceDirection === 'next' ? styles.bounce : undefined}
            onAnimationEnd={() => setBounceDirection(null)}
            data-react-autofocus={autoFocusNext}
          />
          <IconButton
            icon={ChevronUpIcon}
            variant="invisible"
            size="small"
            onClick={e => handleNav('prev', e.detail === 0 ? 'activate' : 'click')}
            inactive={!hasPrev}
            aria-disabled={!hasPrev || undefined}
            aria-label="Previous issue"
            keybindingHint="k"
            className={bounceDirection === 'prev' ? styles.bounce : undefined}
            onAnimationEnd={() => setBounceDirection(null)}
            data-react-autofocus={autoFocusPrev}
          />
        </div>
      </nav>
    </>
  )
}
